import { addEdge } from '@xyflow/react';

export const getRightmostPosition = nodes => {
  return nodes.reduce(
    (acc, n) => {
      const nx = n.position?.x ?? 0;
      return nx > acc.x ? { x: nx, y: n.position?.y ?? acc.y } : acc;
    },
    { x: 0, y: 100 }
  );
};

export const getNextId = counterRef => `node_${counterRef.current++}`;

export const syncIdCounterWithNodes = (nds, counterRef) => {
  let max = 0;
  nds.forEach(n => {
    const match = n.id.match(/^node_(\d+)$/);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  });
  counterRef.current = max + 1;
};

/**
 * Ensures a default end node exists for linear auto-connect flows.
 * If at least one end already exists, only returns the same node list and the first end id (unmoved).
 */
export const ensureEndNode = (nodes, newPos, getId) => {
  const existingEnds = nodes.filter(n => n.type === 'end');
  if (existingEnds.length > 0) {
    const first = existingEnds[0];
    return {
      nextNodes: nodes,
      endId: first.id,
      endNode: first
    };
  }
  const endId = getId();
  const endNode = {
    id: endId,
    type: 'end',
    data: {},
    position: { x: newPos.x + 350, y: newPos.y }
  };
  return { nextNodes: [...nodes, endNode], endId, endNode };
};

export const addRemovableEdge = (source, target, edges) => {
  return addEdge(
    {
      id: `e-${source}-${target}`,
      source,
      target,
      type: 'removableEdge',
      animated: true
    },
    edges
  );
};

/** @param {string} endId - target end node id (one of possibly many end nodes) */
export const rewireEdgesForEndNode = (edges, newId, endId) => {
  if (!endId) return edges;
  const incoming = edges.find(e => e.target === endId);
  let updated = [...edges];
  if (incoming) {
    updated = updated.filter(e => e.target !== endId);
    updated = addRemovableEdge(incoming.source, newId, updated);
  }
  updated = addRemovableEdge(newId, endId, updated);
  return updated;
};

export const normalizeTransitions = (nodeData, newId) => {
  return (
    nodeData?.transitions?.map((t, i) => ({
      ...t,
      id: `transition-${newId}-${i}`
    })) || []
  );
};

export const getRightmostNonEndNode = (nodes, startNode) => {
  return (
    nodes
      .filter(n => n.type !== 'end' && n.type !== 'start')
      .reduce(
        (acc, n) =>
          (n.position?.x ?? 0) > (acc.position?.x ?? -Infinity) ? n : acc,
        { position: { x: -Infinity } }
      ) || startNode
  );
};

export const filterEmptyNodeFields = (nodes, fieldsToFilter = []) => {
  // Default field configurations
  const defaultFieldConfigs = {
    transitions: { property: 'value' },
    userOptions: { property: 'value' },
    variables: { property: 'name' }
  };

  // Merge with custom field configs
  const fieldConfigs = { ...defaultFieldConfigs, ...fieldsToFilter };

  const errors = [];
  const filteredNodes = nodes.map(node => {
    // Check if node has any of the fields we want to filter
    const hasFieldsToFilter = Object.keys(fieldConfigs).some(
      field => node.data?.[field]
    );

    if (hasFieldsToFilter) {
      const updatedData = { ...node.data };

      // Filter each specified field
      Object.entries(fieldConfigs).forEach(([fieldName, config]) => {
        if (node.data[fieldName]) {
          const propertyToCheck = config.property || 'value';
          updatedData[fieldName] = node.data[fieldName].filter(
            item => item[propertyToCheck]?.toString().trim() !== ''
          );
        }
      });

      // Special validation for variables: if this is a variable node and all variables are empty
      if (
        node.type === 'variable' &&
        (!updatedData.variables || updatedData.variables.length === 0)
      ) {
        errors.push(
          'Variable nodes must have at least one configured variable.'
        );
      }

      return {
        ...node,
        data: updatedData
      };
    }
    return node;
  });

  return { nodes: filteredNodes, errors };
};
export const objectToRows = (obj = {}) =>
  Object.entries(obj).map(([key, value]) => ({
    id: key,
    key,
    value
  }));

export const rowsToObject = rows =>
  rows.reduce((acc, { key, value }) => {
    if (!key) return acc;
    acc[key] = value ?? '';
    return acc;
  }, {});

export const flattenObject = (obj, maxDepth = 3) => {
  const result = {};

  const traverse = (current, parentKey = '', depth = 0) => {
    if (depth > maxDepth || typeof current !== 'object' || current === null) {
      return;
    }

    Object.entries(current).forEach(([key, value]) => {
      const newKey = parentKey ? `${key}` : key;

      // if value is object go deeper
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        depth < maxDepth
      ) {
        traverse(value, newKey, depth + 1);
      } else {
        result[newKey] = newKey;
      }
    });
  };

  traverse(obj);

  return result;
};

/**
 * Stops keyboard event propagation for clipboard shortcuts (Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A)
 * to prevent ReactFlow from intercepting them and allow browser native behavior.
 * Use this as onKeyDown handler for text inputs inside ReactFlow nodes.
 * @param {KeyboardEvent} event - The keyboard event
 */
export const handleClipboardKeyDown = event => {
  if (
    (event.ctrlKey || event.metaKey) &&
    (event.key === 'v' || event.key === 'c' || event.key === 'x' || event.key === 'a')
  ) {
    event.stopPropagation();
  }
};
